import type { Components } from '@/api/generated/api';
import SupportMeasureEntryDialog from '@/core/supportMeasures/SupportMeasureEntryDialog.vue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { DialogQueuePriority } from '@/core/dialogsystem/dialogQueue';
import { useSupportMeasureCheckout } from '@/composables/supportMeasureEntry/useSupportMeasureCheckout';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

interface IUseSupportMeasureEntryDialog {
  showSupportMeasureEntryDialog: (entryId: number) => void;
  showSupportMeasureEntryDialogQueued: (entryId: number) => void;
  showProductCheckoutDialog: (feature?: Components.Schemas.ProductFeature | null) => void;
}

interface IUseSupportMeasureEntryDialogOptions {
  /** Opens the entry read-only (archived class / school context) — every write is rejected server-side. */
  disabled?: MaybeRefOrGetter<boolean>;
  onClose?: () => Promise<void> | void;
}

export const useSupportMeasureEntryDialog = (
  options: IUseSupportMeasureEntryDialogOptions = {},
): IUseSupportMeasureEntryDialog => {
  const { showProductCheckoutDialog } = useSupportMeasureCheckout();

  const detailDialog = useDialog({
    component: SupportMeasureEntryDialog,
    props: computed(() => ({ disabled: toValue(options.disabled) ?? false, onClose: options.onClose })),
  });

  const showSupportMeasureEntryDialog = (entryId: number): void => {
    void detailDialog.show({ entryId });
  };

  const showSupportMeasureEntryDialogQueued = (entryId: number): void => {
    void detailDialog.showQueued(
      {
        dedupeKey: `support-measure-entry-details:${entryId}`,
        priority: DialogQueuePriority.ProductFlow,
      },
      { entryId },
    );
  };

  return { showSupportMeasureEntryDialog, showSupportMeasureEntryDialogQueued, showProductCheckoutDialog };
};
